import { get_token } from "@/router/index.js";
import DriveWebSocketClient from "@/services/drive_websocket";

const DRIVE_EVENT_METHODS = new Map([
  ["file_uploaded", "handle_drive_item_added_event"],
  ["folder_created", "handle_drive_item_added_event"],
  ["drive_item_renamed", "handle_drive_item_renamed_event"],
  ["drive_item_moved", "handle_drive_item_moved_event"],
  ["drive_item_deleted", "handle_drive_item_removed_event"],
  ["drive_item_restored", "handle_drive_item_refresh_event"],
  ["drive_item_shared", "handle_drive_item_shared_event"],
  ["drive_item_unshared", "handle_drive_item_unshared_event"],
  ["drive_permission_updated", "handle_drive_permission_updated_event"],
  ["drive_counts_updated", "handle_drive_counts_updated_event"],
]);

const DRIVE_EVENT_TYPE_KEYS = Object.freeze(["type", "event_type", "event"]);
const DRIVE_EVENT_ITEM_KEYS = Object.freeze(["drive_item", "item", "file", "folder"]);
const DRIVE_EVENT_ID_KEYS = Object.freeze(["drive_item_id", "item_id", "file_id", "folder_id", "id"]);
const DRIVE_EVENT_NAME_KEYS = Object.freeze(["name", "new_name", "filename", "folder_name"]);
const LOCAL_UPDATE_SECTIONS = Object.freeze(["my", "shared"]);

function event_data(event) {
  const data = Reflect.get(event || {}, "data");
  return data && typeof data === "object" ? data : event;
}

function first_present_value(source, keys) {
  if (!source || typeof source !== "object") return null;
  const key = keys.find((item_key) => Reflect.get(source, item_key) !== undefined);
  return key ? Reflect.get(source, key) : null;
}

function drive_event_type(event) {
  return first_present_value(event, DRIVE_EVENT_TYPE_KEYS)
    || first_present_value(event_data(event), DRIVE_EVENT_TYPE_KEYS);
}

function drive_event_item(event) {
  return first_present_value(event_data(event), DRIVE_EVENT_ITEM_KEYS)
    || first_present_value(event, DRIVE_EVENT_ITEM_KEYS);
}

function drive_event_id(event) {
  const item = drive_event_item(event);
  return first_present_value(item, ["id"])
    || first_present_value(event_data(event), DRIVE_EVENT_ID_KEYS)
    || first_present_value(event, DRIVE_EVENT_ID_KEYS);
}

function drive_event_name(event) {
  const item = drive_event_item(event);
  return first_present_value(item, DRIVE_EVENT_NAME_KEYS)
    || first_present_value(event_data(event), DRIVE_EVENT_NAME_KEYS)
    || first_present_value(event, DRIVE_EVENT_NAME_KEYS);
}

function same_drive_item_id(item, item_id) {
  return String(item?.id) === String(item_id);
}

export const drive_websocket_methods = {
  connect_drive_socket() {
    const token = get_token();
    if (!token) return;
    if (this.drive_socket) {
      this.drive_socket.connect();
      return;
    }

    this.drive_socket = new DriveWebSocketClient({
      token,
      on_event: (event) => this.handle_drive_socket_event(event),
      on_open: () => this.handle_drive_socket_open(),
      on_close: () => this.handle_drive_socket_close(),
      on_error: () => this.handle_drive_socket_error(),
    });
    this.drive_socket.connect();
  },

  disconnect_drive_socket() {
    if (!this.drive_socket) return;
    this.drive_socket.disconnect();
    this.drive_socket = null;
    this.drive_socket_connected = false;
  },

  handle_drive_socket_open() {
    this.drive_socket_connected = true;
    this.drive_socket_last_error = null;
  },

  handle_drive_socket_close() {
    this.drive_socket_connected = false;
  },

  handle_drive_socket_error() {
    this.drive_socket_last_error = "Drive realtime connection interrupted.";
  },

  handle_drive_socket_event(event) {
    const method_name = DRIVE_EVENT_METHODS.get(drive_event_type(event));
    if (!method_name) return;

    const handler = Reflect.get(this, method_name);
    if (typeof handler === "function") handler.call(this, event);
  },

  normalized_drive_socket_item(event) {
    const item = drive_event_item(event);
    if (!item || typeof item !== "object") return null;
    return this.normalize_drive_item(item);
  },

  drive_socket_item_visible(item) {
    if (!LOCAL_UPDATE_SECTIONS.includes(this.drive_section)) return false;
    if (this.current_folder_id !== null) return this.is_child_of_current_folder(item);
    return this.is_top_level_item(item, this.section_items.concat([item]));
  },

  upsert_drive_socket_item(item) {
    this.upsert_local_drive_item(item, { id: item.id });
  },

  refresh_drive_socket_counts() {
    this.schedule_active_drive_refresh({ include_storage: true });
  },

  handle_drive_item_added_event(event) {
    const item = this.normalized_drive_socket_item(event);
    if (!item || !this.drive_socket_item_visible(item)) {
      this.refresh_drive_socket_counts();
      return;
    }

    this.upsert_drive_socket_item(item);
    this.fetch_storage_stats();
  },

  handle_drive_item_renamed_event(event) {
    const item_id = drive_event_id(event);
    const name = drive_event_name(event);
    if (!item_id || !name) {
      this.schedule_active_drive_refresh();
      return;
    }

    this.patch_visible_drive_socket_item(item_id, { name });
  },

  handle_drive_item_moved_event(event) {
    const item = this.normalized_drive_socket_item(event);
    if (!item) {
      this.schedule_active_drive_refresh();
      return;
    }

    this.apply_moved_drive_socket_item(item);
  },

  apply_moved_drive_socket_item(item) {
    if (this.drive_socket_item_visible(item)) {
      this.upsert_drive_socket_item(item);
      return;
    }

    this.remove_local_drive_item(item.id);
  },

  handle_drive_item_removed_event(event) {
    const item_id = drive_event_id(event);
    if (!item_id) {
      this.refresh_drive_socket_counts();
      return;
    }

    this.remove_local_drive_item(item_id);
    this.fetch_storage_stats();
  },

  handle_drive_item_refresh_event() {
    this.refresh_drive_socket_counts();
  },

  handle_drive_item_shared_event(event) {
    const item = this.normalized_drive_socket_item(event);
    if (!item) {
      this.refresh_drive_socket_counts();
      return;
    }

    this.handle_shared_drive_socket_item(item);
  },

  handle_shared_drive_socket_item(item) {
    if (this.drive_socket_item_visible(item)) {
      this.upsert_drive_socket_item(item);
      return;
    }

    this.refresh_drive_socket_counts();
  },

  handle_drive_item_unshared_event(event) {
    const item_id = drive_event_id(event);
    if (!item_id) {
      this.refresh_drive_socket_counts();
      return;
    }

    if (this.drive_section === "shared") this.remove_local_drive_item(item_id);
    this.refresh_drive_socket_counts();
  },

  handle_drive_permission_updated_event(event) {
    const item = this.normalized_drive_socket_item(event);
    if (!item) {
      this.schedule_active_drive_refresh();
      return;
    }

    this.upsert_drive_socket_item(item);
  },

  handle_drive_counts_updated_event() {
    this.refresh_drive_socket_counts();
  },

  patch_visible_drive_socket_item(item_id, patch) {
    this.drive_items = this.drive_items.map((item) => {
      if (!same_drive_item_id(item, item_id)) return item;
      return ({ ...item, ...patch });
    });
  },
};
